import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Crown, Flame, Gift } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { proDaysState } from '@/lib/proDays';
import { trackHeaderBar } from '@/lib/headerBars';
import { useProCta } from '@/hooks/useDoubleBenefit';

/**
 * Живой счётчик подарочных дней (правило — lib/proDays.js): у пробного
 * доступа — дни ЛАЙТ и ссылка на программу ПРО дня, у оплативших — «ПРО до …».
 *
 * variant="strip" — тонкая полоса в шапке сайта, ПОД меню (ProDaysStrip
 *                   ниже, его рисует MainNavbar внутри <nav>);
 * variant="card"  — плашка в личном кабинете.
 *
 * Пересчитывается раз в минуту: вкладка, открытая с вечера, утром покажет
 * на день меньше без перезагрузки. Цвета — токены темы (theme-*), поэтому
 * на рф полоса голубая, на ру сиреневая; «последние дни» — статусный
 * янтарно-красный, он одинаков на обоих сайтах.
 */
export function useProDays() {
  const { user, isAuthenticated, loading } = useAuth();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);
  if (loading || !isAuthenticated) return null;
  return proDaysState(user, now);
}

/**
 * Полоса счётчика под строкой меню. Стоит внутри фиксированного <nav>, её
 * высота уходит в --promo-bar-h (lib/headerBars.js): документ съезжает
 * вниз ровно на неё, и отступы страниц под шапкой не меняются.
 */
export function ProDaysStrip() {
  const state = useProDays();
  const ref = useRef(null);
  const key = state ? state.text + (state.urgent ? '!' : '') : '';
  useEffect(() => trackHeaderBar('days', ref.current), [key]);
  if (!state) return null;
  return (
    <div ref={ref} data-testid="pro-days-under-menu">
      <ProDaysCounter state={state} variant="strip" />
    </div>
  );
}

export default function ProDaysCounter({ state, variant = 'card', className = '' }) {
  const own = useProDays();
  // Куда ведёт «Оформить ПРО»: тот же вход в ПРО, что у всех замков
  // (с «ДВОЙНОЙ ВЫГОДОЙ» — сразу на её оплату).
  const proCta = useProCta();
  const s = state === undefined ? own : state;
  if (!s) return null;

  // «Последний день» — та же полоса подарка, сильнее и с кнопкой ПРО.
  const isTrial = s.kind === 'trial' || s.kind === 'last';
  const Icon = isTrial ? (s.urgent ? Flame : Gift) : Crown;

  if (variant === 'strip') {
    const tone = !isTrial
      ? 'bg-theme-50 text-theme-800 border-b border-theme-100'
      : s.urgent
        ? 'bg-rose-600 text-white font-bold'
        : 'bg-theme-600 text-white font-semibold';
    // Ссылка на программу ПРО — только с 640 px: на телефоне вместе с
    // текстом счётчика она переносилась бы в три строки.
    return (
      <div
        className={`w-full ${tone} ${className}`}
        data-testid="pro-days-strip"
        data-kind={s.kind}
        data-urgent={s.urgent ? '1' : '0'}
      >
        <div className="max-w-6xl mx-auto px-3 py-1 flex items-center justify-center gap-x-3 gap-y-0.5 flex-wrap text-xs sm:text-sm leading-snug text-center">
          <Link to={isTrial ? '/pricing' : '/account'} className="inline-flex items-center gap-1.5">
            <Icon className={`w-3.5 h-3.5 shrink-0 ${s.urgent ? 'animate-pulse' : ''}`} aria-hidden="true" />
            <span>{s.text}</span>
          </Link>
          {s.spotlight && (
            <Link
              to={s.spotlight.path}
              className="hidden sm:inline font-semibold underline underline-offset-2 whitespace-nowrap"
              data-testid="pro-days-spotlight"
            >
              {s.spotlight.text}
            </Link>
          )}
          {s.cta && (
            <Link
              to={proCta.path}
              className="bg-white text-rose-700 font-bold rounded-md px-2.5 py-0.5 whitespace-nowrap hover:bg-rose-50"
              data-testid="pro-days-cta"
            >
              {proCta.isPro5 ? proCta.label : s.cta} →
            </Link>
          )}
        </div>
      </div>
    );
  }

  const tone = !isTrial
    ? 'bg-theme-50 border-theme-200 text-theme-800'
    : s.urgent
      ? 'bg-rose-50 border-rose-300 text-rose-700 ring-2 ring-rose-200'
      : 'bg-theme-50 border-theme-300 text-theme-800';
  return (
    <div
      className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 ${tone} ${className}`}
      data-testid="pro-days-card"
      data-kind={s.kind}
      data-urgent={s.urgent ? '1' : '0'}
    >
      <Icon className={`w-5 h-5 shrink-0 ${s.urgent ? 'animate-pulse' : ''}`} aria-hidden="true" />
      <span className="min-w-0">
        <span className={`block text-sm sm:text-base leading-snug ${isTrial ? 'font-bold' : 'font-semibold'}`}>
          {s.text}
        </span>
        {s.cta && (
          <Link
            to={proCta.path}
            className="inline-block mt-1 bg-rose-600 text-white text-xs sm:text-sm font-bold rounded-md px-2.5 py-1 hover:bg-rose-700"
            data-testid="pro-days-cta"
          >
            {proCta.isPro5 ? proCta.label : s.cta} →
          </Link>
        )}
        {s.spotlight && (
          <Link
            to={s.spotlight.path}
            className="block mt-0.5 text-xs sm:text-sm font-semibold text-theme-700 underline underline-offset-2"
            data-testid="pro-days-spotlight"
          >
            {s.spotlight.text}
          </Link>
        )}
      </span>
    </div>
  );
}
